import { createAsyncThunk, createSlice, } from '@reduxjs/toolkit'
import { AppState } from '@app/store/state/app.state';
import { createOrUpdateOffice, OfficeEntity, OfficeType, } from '@features/desks/services/AdvanceHotDeskingService';
import { fetchOfficeEntities } from "./advanceHotDeskingSetup.duck";
import { failureNotification } from "@shared/store/notification.duck";
import { Error } from "@shared/utils/error-helper";
import { redirectTo } from "@shared/store/auth.duck";
import { Desk } from '@features/desks/services/DeskBookingService';
import {
  OfficeManagementPanelView,
  OfficeInformationView
} from "@features/settings/pages/office-management-page-v2/data/OfficeManagement.model";
import { getParameterByName } from "@shared/utils/UrlUtils";

function getActivePanelWithFallback() {
  const param = getParameterByName('panel', window.location.href);
  const parsed = param ? parseInt(param) : NaN;
  return Number.isNaN(parsed) ? OfficeInformationView.OfficeInformation : parsed;
}

export interface EditOfficeLayout {
  editingOffice?: OfficeEntity;
  focusedChildOffice?: OfficeEntity;
  editingOfficeDesks: Desk[];
  floors: OfficeEntity[];
  loading: boolean;
  activePanel: OfficeInformationView;
  activeOfficeManagementPanel: OfficeManagementPanelView;
  error?: Error;
}

export const initialEditOfficeLayout: EditOfficeLayout = {
  floors: [],
  editingOfficeDesks: [],
  activePanel: getActivePanelWithFallback(),
  activeOfficeManagementPanel: OfficeManagementPanelView.OfficeManagement,
  loading: false,
}

export const addFloorToOffice: any = createAsyncThunk(
  'editOfficeLayout/addFloorToOffice',
  async (parentOfficeId: number, thunkAPI) => {
    return {
      id: 0,
      label: '',
      capacity: '',
      officeId: 0,
      parentId: parentOfficeId
    }
  }
);

export const updateOfficeFloor: any = createAsyncThunk(
  'editOfficeLayout/updateOfficeFloor',
  async (props: {floor: OfficeEntity, field: string, value: any}, thunkAPI) => {
    const appState = (thunkAPI.getState() as AppState);
    const idx = appState.editOfficeLayout.floors.findIndex((o: OfficeEntity) => o.id === props.floor.id);
    if (idx > -1) {
      let floorToUpdate = {
        ...appState.editOfficeLayout.floors[idx],
      };
      // @ts-ignore
      floorToUpdate[props.field] = props.value;
      let updatedArray = [...appState.editOfficeLayout.floors];
      updatedArray.splice(idx, 1, floorToUpdate);
      return updatedArray
    }
    return appState.editOfficeLayout.floors;
  }
);

export const createOrUpdateOfficeLayout: any = createAsyncThunk(
  'editOfficeLayout/createOrUpdateOfficeLayout',
  async (office: OfficeEntity, thunkAPI) => {
    try {
      const editingFloors = ((thunkAPI.getState() as AppState).editOfficeLayout.floors);
      const newOffice = await createOrUpdateOffice({
        id: office.id,
        name: office.label,
        postCode: office.postCode,
        threeLetterCode: office.threeLetterCode,
        capacity: office.capacity,
        floors: editingFloors,
        parentId: 0,
        officeType: OfficeType.OFFICE, //TODO Change to be dynamic
        capacityEnabled: office.capacityEnabled || false,
        carParkCapacity: office?.carParkCapacity,
        officeBookingWindowWeeks: office.officeBookingWindowWeeks,
        restrictBookingWindowForTeamAdmins: office.restrictBookingWindowForTeamAdmins,
        bookingWindowOpenDay: office.bookingWindowOpenDay,
        bookingWindowOpenTime: office.bookingWindowOpenTime,
        bookingWindowTimezone: office.bookingWindowTimezone,
      });

      await thunkAPI.dispatch(fetchOfficeEntities());
      await new Promise(((resolve: any) => setTimeout(() => resolve(), 500)));
      if (office.id) {
        // await thunkAPI.dispatch(setEditingOffice(selectOfficeById(thunkAPI.getState() as AppState, office.parentId)));
      } else {
        thunkAPI.dispatch(resetEditOfficeLayout());
        thunkAPI.dispatch(redirectTo(`/manage/offices/${newOffice.id}`));
      }
    } catch (e: any) {
      thunkAPI.dispatch(failureNotification('Failed to update office'));
    }
  }
);

const editOfficeLayoutSlice = createSlice({
  name: 'editOfficeLayout',
  initialState: initialEditOfficeLayout,
  reducers: {
    resetEditOfficeLayout: () => ({...initialEditOfficeLayout}),
    setActivePanel: (state, action) => ({...state, activePanel: action.payload}),
    setActiveOfficeManagementPanel: (state, action) => ({...state, activeOfficeManagementPanel: action.payload}),
    setFocusedChildOffice: (state, action) => ({...state, focusedChildOffice: action.payload}),
    setError: (state, action) => ({...state, error: action.payload}),
    setEditingFloors: (state, action) => ({...state, floors: action.payload}),
    setEditingDesks: (state, action) => ({...state, editingOfficeDesks: action.payload}),
    addEditingDesks: (state, action) => ({...state, editingOfficeDesks: [...state.editingOfficeDesks, ...action.payload]}),
    setEditingOffice: (state, action) => ({
      ...initialEditOfficeLayout,
      activePanel: state.activePanel,
      editingOffice: action.payload.office,
      floors: action.payload?.children ?? [],
      editingOfficeDesks: [],
    }),
  },
  extraReducers: (builder) => {
    builder
      .addCase(createOrUpdateOfficeLayout.pending, (state) => ({...state, loading: true}))
      .addCase(createOrUpdateOfficeLayout.rejected, (state) => ({...state, loading: false}))
      .addCase(createOrUpdateOfficeLayout.fulfilled, (state) => ({...state, loading: false}))
      .addCase(addFloorToOffice.fulfilled, (state, action) => ({...state, floors: [...state.floors, action.payload]}))
      .addCase(updateOfficeFloor.fulfilled, (state, action) => ({...state, floors: action.payload}));
  }
})

export default editOfficeLayoutSlice.reducer;
export const { resetEditOfficeLayout, setEditingOffice, setActivePanel, setActiveOfficeManagementPanel, setFocusedChildOffice, setEditingFloors, addEditingDesks } = editOfficeLayoutSlice.actions;

export const selectEditingOffice = (state: AppState) => state.editOfficeLayout.editingOffice;
export const selectAllDesksInEditingOffice = (state: AppState) => state.editOfficeLayout.editingOfficeDesks;
export const selectFloors = (state: AppState) => state.editOfficeLayout.floors;
export const selectIsLoading = (state: AppState) => state.editOfficeLayout.loading;
export const selectEditOfficeError = (state: AppState) => state.editOfficeLayout.error;
export const selectActivePanel = (state: AppState) => state.editOfficeLayout.activePanel;
export const selectActiveOfficeManagementPanel = (state: AppState) => state.editOfficeLayout.activeOfficeManagementPanel;
export const selectFocusedChildOffice = (state: AppState) => state.editOfficeLayout.focusedChildOffice;
